/**
 * List view column definitions and defaults.
 */

export const LIST_VIEW_KEYS = {
  JOBS_STAFF: 'jobs-staff',
  JOBS_CANDIDATE: 'jobs-candidate',
  CANDIDATES: 'candidates',
  USERS: 'users',
};

export const JOB_COLUMNS_STAFF = [
  { id: 'select', label: 'Select', locked: true, width: 40, minWidth: 40 },
  { id: 'company', label: 'Company', defaultVisible: true, width: 150, minWidth: 100 },
  { id: 'title', label: 'Job Title', defaultVisible: true, width: 250, minWidth: 140 },
  { id: 'location', label: 'Location', defaultVisible: true, width: 180, minWidth: 90 },
  { id: 'employment_type', label: 'Type', defaultVisible: false, width: 100, minWidth: 80 },
  { id: 'salary', label: 'Salary', defaultVisible: true, width: 120, minWidth: 96 },
  { id: 'match_coverage', label: 'Match coverage', defaultVisible: true, width: 108, minWidth: 88 },
  { id: 'listing', label: 'Listing', defaultVisible: false, width: 100, minWidth: 88 },
  { id: 'status', label: 'Status', defaultVisible: true, width: 100, minWidth: 88 },
];

export const JOB_COLUMNS_CANDIDATE = [
  { id: 'select', label: 'Select', locked: true, width: 40, minWidth: 40 },
  { id: 'company', label: 'Company', defaultVisible: true, width: 150, minWidth: 100 },
  { id: 'title', label: 'Job Title', defaultVisible: true, width: 250, minWidth: 140 },
  { id: 'location', label: 'Location', defaultVisible: true, width: 180, minWidth: 90 },
  { id: 'employment_type', label: 'Type', defaultVisible: false, width: 100, minWidth: 80 },
  { id: 'salary', label: 'Salary', defaultVisible: true, width: 120, minWidth: 96 },
  { id: 'match_score', label: 'Match', defaultVisible: true, width: 96, minWidth: 80 },
  { id: 'status', label: 'Status', defaultVisible: true, width: 118, minWidth: 108 },
];

export const CANDIDATE_COLUMNS = [
  { id: 'select', label: 'Select', locked: true, width: 44, minWidth: 44 },
  { id: 'name', label: 'Name', defaultVisible: true, width: 180, minWidth: 100 },
  { id: 'email', label: 'Email', defaultVisible: true, width: 200, minWidth: 120 },
  { id: 'phone', label: 'Phone', defaultVisible: false, width: 150, minWidth: 90 },
  { id: 'current_position', label: 'Current Position', defaultVisible: true, width: 250, minWidth: 140 },
  { id: 'experience', label: 'Experience', defaultVisible: true, width: 120, minWidth: 80 },
  { id: 'resumes', label: 'Resumes', defaultVisible: false, width: 100, minWidth: 80 },
  { id: 'matches', label: 'Matches', defaultVisible: true, width: 100, minWidth: 80 },
  { id: 'package', label: 'Package', defaultVisible: true, width: 120, minWidth: 96 },
  { id: 'status', label: 'Status', defaultVisible: true, width: 150, minWidth: 100 },
];

export const USER_COLUMNS = [
  { id: 'name', label: 'Name', defaultVisible: true, width: 180, minWidth: 100 },
  { id: 'email', label: 'Email', defaultVisible: true, width: 220, minWidth: 120 },
  { id: 'role', label: 'Role', defaultVisible: true, width: 110, minWidth: 80 },
  { id: 'phone', label: 'Phone', defaultVisible: false, width: 120, minWidth: 90 },
  { id: 'status', label: 'Status', defaultVisible: true, width: 100, minWidth: 80 },
];

export function getDefaultVisibleColumnIds(columns) {
  return columns.filter((c) => c.defaultVisible || c.locked).map((c) => c.id);
}

export function getLockedColumnIds(columns) {
  return columns.filter((c) => c.locked).map((c) => c.id);
}

/**
 * Preserve user column order for unlocked columns.
 * Locked columns always lead (catalog order). Unknown ids are dropped.
 */
export function normalizeVisibleColumns(columnIds, allColumns, lockedIds, fallbackDefaultIds) {
  const catalogOrder = allColumns.map((c) => c.id);
  const allowed = new Set(catalogOrder);
  const lockedSet = new Set(lockedIds);
  const source =
    Array.isArray(columnIds) && columnIds.length > 0 ? columnIds : fallbackDefaultIds;

  const unlocked = [];
  const seen = new Set();
  for (const id of source) {
    if (!allowed.has(id) || lockedSet.has(id) || seen.has(id)) continue;
    unlocked.push(id);
    seen.add(id);
  }

  const lockedOrdered = catalogOrder.filter((id) => lockedSet.has(id));
  return [...lockedOrdered, ...unlocked];
}

export function widthsForVisibleColumns(visibleIds, allColumns) {
  const byId = Object.fromEntries(allColumns.map((c) => [c.id, c]));
  return visibleIds.map((id) => byId[id]?.width ?? 120);
}

export function minWidthsForVisibleColumns(visibleIds, allColumns) {
  const byId = Object.fromEntries(allColumns.map((c) => [c.id, c]));
  return visibleIds.map((id) => byId[id]?.minWidth ?? 80);
}

export function toggleableColumns(allColumns) {
  return allColumns.filter((c) => !c.locked);
}
